import { useEffect, useRef, useState, FormEvent } from 'react';

export default function BookingSection() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setStatus('error');
      return;
    }
    const data = new URLSearchParams();
    new FormData(form).forEach((v, k) => data.append(k, v as string));
    try {
      const res = await fetch('https://readdy.ai/api/form/d76h4i54hjod66j2eqj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass = 'w-full font-noto text-sm text-[#2c2218] bg-white border border-[#e8ddd0] rounded-lg px-4 py-3 focus:outline-none focus:border-[#c4956a] transition-colors placeholder-[#b8a898]';

  return (
    <section id="booking" ref={ref} className="py-24 lg:py-36 px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        {/* Left: info */}
        <div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
          <p className="font-noto text-xs tracking-[0.4em] text-[#c4956a] uppercase mb-6">Reservation</p>
          <h2 className="font-cormorant text-5xl lg:text-6xl text-[#2c2218] font-light leading-tight mb-8">
            ご予約・<br />
            <span className="italic">お問い合わせ</span>
          </h2>
          <p className="font-noto text-sm text-[#6b5c4e] leading-relaxed mb-12">
            ウェディングフラワーのご相談や各種フラワーサービスへのご予約を承ります。まずはお気軽にお問い合わせください。専任スタッフがご対応いたします。
          </p>

          <div className="space-y-6">
            {[
              { icon: 'ri-time-line', title: '営業時間', text: '火〜土 10:00 – 18:00\n日・月 定休日' },
              { icon: 'ri-map-pin-line', title: 'スタジオ', text: '東京都渋谷区神宮前 3-12-8\n表参道駅 徒歩5分' },
              { icon: 'ri-phone-line', title: 'お電話', text: '03-5678-9012' },
              { icon: 'ri-mail-line', title: 'メール', text: 'hello@bloom-flower.jp' },
            ].map(item => (
              <div key={item.title} className="flex gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#f0e8de] rounded-full flex-shrink-0">
                  <i className={`${item.icon} text-[#c4956a]`}></i>
                </div>
                <div>
                  <p className="font-noto text-xs tracking-wider text-[#8b7355] mb-1">{item.title}</p>
                  <p className="font-noto text-sm text-[#2c2218] whitespace-pre-line">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: form */}
        <div className={`transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
          <div className="bg-[#faf8f5] rounded-2xl p-8 lg:p-10">
            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-[#f0e8de] rounded-full mx-auto mb-6">
                  <i className="ri-check-line text-[#c4956a] text-2xl"></i>
                </div>
                <h3 className="font-cormorant text-3xl text-[#2c2218] font-light mb-4">送信完了</h3>
                <p className="font-noto text-sm text-[#6b5c4e] leading-relaxed">
                  お問い合わせありがとうございます。<br />
                  2営業日以内にご連絡いたします。
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-8 font-noto text-sm tracking-widest text-[#c4956a] border border-[#c4956a] px-8 py-2 rounded-full hover:bg-[#c4956a] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  戻る
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-noto text-xs tracking-wider text-[#6b5c4e] mb-1.5 block">お名前 *</label>
                    <input type="text" name="name" required placeholder="山田 花子" className={inputClass} />
                  </div>
                  <div>
                    <label className="font-noto text-xs tracking-wider text-[#6b5c4e] mb-1.5 block">メールアドレス *</label>
                    <input type="email" name="email" required placeholder="hello@example.com" className={inputClass} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-noto text-xs tracking-wider text-[#6b5c4e] mb-1.5 block">電話番号</label>
                    <input type="tel" name="phone" placeholder="090-0000-0000" className={inputClass} />
                  </div>
                  <div>
                    <label className="font-noto text-xs tracking-wider text-[#6b5c4e] mb-1.5 block">ご希望日</label>
                    <input type="date" name="preferred_date" className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className="font-noto text-xs tracking-wider text-[#6b5c4e] mb-1.5 block">サービス種別</label>
                  <select name="service_type" className={inputClass}>
                    <option value="">選択してください</option>
                    <option value="ブライダルフラワー">ブライダルフラワー</option>
                    <option value="フラワーギフト">フラワーギフト</option>
                    <option value="インテリアフラワー">インテリアフラワー</option>
                    <option value="フラワーレッスン">フラワーレッスン</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
                <div>
                  <label className="font-noto text-xs tracking-wider text-[#6b5c4e] mb-1.5 block">
                    ご要望・ご質問 <span className="text-[#b8a898] font-light">（500文字以内）</span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="ご希望の花の雰囲気やご予算などをお気軽にお知らせください。"
                    className={`${inputClass} resize-none`}
                  />
                </div>

                {status === 'error' && (
                  <p className="font-noto text-xs text-red-500">送信に失敗しました。もう一度お試しください。</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full font-noto text-sm tracking-widest bg-[#2c2218] text-[#faf8f5] py-3.5 rounded-full hover:bg-[#c4956a] transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {status === 'sending' ? '送信中...' : '送信する'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
